import React, { useState } from 'react';
import { 
  AlertTriangle, 
  Shield, 
  CheckCircle2, 
  XCircle, 
  Edit3, 
  Plus, 
  Brain, 
  Eye, 
  Save, 
  X,
  Calendar,
  User,
  Building,
  Activity
} from 'lucide-react';

interface Risk {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL';
  taxonomy: string[];
  aiGenerated?: boolean;
  inherentLikelihood?: number;
  inherentImpact?: number;
  inherentRiskScore?: number;
}

interface Control {
  id: string;
  code: string;
  name: string;
  description: string;
  type: string;
  frequency: string;
  owner: string;
  lastTested: string;
  effectiveness: 'Effective' | 'Needs Review' | 'Ineffective';
  regulatory: {
    framework: string;
    reference: string;
    description: string;
  }[];
  issues?: {
    identified: string;
    remediationDue: string;
  };
  aiGenerated?: boolean;
}

interface ProcessStepDetailCardProps {
  processStep: {
    id: number;
    stepNumber: number;
    name: string;
    description: string;
    type: string;
    riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL';
    effectiveControls: number;
    totalControls: number;
  };
  risks: Risk[];
  controls: Control[];
  onEditStep: () => void;
  onAddRisk: () => void;
  onAddControl: () => void;
  onSuggestControls: () => void;
  onViewAuditTrail: () => void;
  onUpdateRisk: (riskId: string, updates: Partial<Risk>) => void;
  onUpdateControl: (controlId: string, updates: Partial<Control>) => void;
  onDeleteRisk: (riskId: string) => void;
  onDeleteControl: (controlId: string) => void;
  isAddingRisk?: boolean;
  isAddingControl?: boolean;
  isSuggestingControls?: boolean;
}

export const ProcessStepDetailCard: React.FC<ProcessStepDetailCardProps> = ({
  processStep,
  risks,
  controls,
  onEditStep,
  onAddRisk,
  onAddControl,
  onSuggestControls,
  onViewAuditTrail,
  onUpdateRisk,
  onUpdateControl,
  onDeleteRisk,
  onDeleteControl,
  isAddingRisk = false,
  isAddingControl = false,
  isSuggestingControls = false
}) => {
  const [editingRisk, setEditingRisk] = useState<string | null>(null);
  const [editingControl, setEditingControl] = useState<string | null>(null);
  const [risksReviewed, setRisksReviewed] = useState(false);
  const [controlsReviewed, setControlsReviewed] = useState(false);

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEffectivenessColor = (effectiveness: string) => {
    switch (effectiveness) {
      case 'Effective': return 'bg-green-100 text-green-800';
      case 'Needs Review': return 'bg-yellow-100 text-yellow-800';
      case 'Ineffective': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const EditableText: React.FC<{
    value: string;
    onSave: (value: string) => void;
    multiline?: boolean;
    className?: string;
  }> = ({ value, onSave, multiline = false, className = "" }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleSave = () => {
      onSave(editValue);
      setIsEditing(false);
    };

    if (isEditing) {
      return (
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className={`flex-1 px-2 py-1 border border-blue-300 rounded text-sm ${className}`}
              rows={3}
            />
          ) : (
            <input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className={`flex-1 px-2 py-1 border border-blue-300 rounded text-sm ${className}`}
            />
          )}
          <button
            onClick={handleSave}
            className="p-1 text-green-600 hover:text-green-800"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setEditValue(value);
              setIsEditing(false);
            }}
            className="p-1 text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }

    return (
      <div 
        className={`group cursor-pointer hover:bg-gray-50 rounded p-1 ${className}`}
        onClick={() => setIsEditing(true)}
      >
        <span>{value}</span>
        <Edit3 className="w-3 h-3 ml-2 opacity-0 group-hover:opacity-100 inline transition-opacity" />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {processStep.stepNumber}
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                {processStep.name}
              </h3>
            </div>
            <p className="text-gray-600 text-sm mb-3">{processStep.description}</p>
            
            {/* Risk Categories Tags */}
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                Operational Risk
              </span>
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                Financial Crime Risk
              </span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                KYC/AML
              </span>
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                3 Controls
              </span>
            </div>
          </div>
          
          <div className="text-right">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRiskLevelColor(processStep.riskLevel)}`}>
              {processStep.riskLevel} RISK
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {processStep.effectiveControls}/{processStep.totalControls} Controls Effective
            </div>
          </div>
        </div>
      </div>

      {/* Associated Risks */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Associated Risks</h4>
          <button
            onClick={onAddRisk}
            disabled={isAddingRisk}
            className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAddingRisk ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                Adding Risk...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Risk
              </>
            )}
          </button>
        </div>
        
        <div className="space-y-4">
          {risks.map((risk) => (
            <div key={risk.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500">
                      {risk.taxonomy.join(' > ')}
                    </span>
                    {risk.aiGenerated && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium">
                        <Brain className="w-3 h-3 inline mr-1" />
                        AI
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-gray-900">{risk.code}: {risk.name}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskLevelColor(risk.riskLevel)}`}>
                      {risk.riskLevel}
                    </span>
                  </div>
                  
                  {/* Risk Assessment Scores */}
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Likelihood:</span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`w-3 h-3 rounded-full ${
                              level <= (risk.inherentLikelihood || 3)
                                ? 'bg-yellow-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                        <span className="ml-1 font-medium">
                          {risk.inherentLikelihood || 3}/5
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Impact:</span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`w-3 h-3 rounded-full ${
                              level <= (risk.inherentImpact || 3)
                                ? 'bg-red-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                        <span className="ml-1 font-medium">
                          {risk.inherentImpact || 3}/5
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Risk Score:</span>
                      <div className={`px-2 py-1 rounded font-medium text-sm ${
                        (risk.inherentRiskScore || 9) >= 20 ? 'bg-red-100 text-red-800' :
                        (risk.inherentRiskScore || 9) >= 15 ? 'bg-orange-100 text-orange-800' :
                        (risk.inherentRiskScore || 9) >= 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {risk.inherentRiskScore || 9}/25
                      </div>
                    </div>
                  </div>
                  <EditableText
                    value={risk.description}
                    onSave={(value) => onUpdateRisk(risk.id, { description: value })}
                    multiline
                    className="text-sm text-gray-600"
                  />
                </div>
                <button
                  onClick={() => onDeleteRisk(risk.id)}
                  className="p-1 text-red-400 hover:text-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Controls */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Key Controls</h4>
          <div className="flex gap-2">
            <button
              onClick={onSuggestControls}
              disabled={isSuggestingControls}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSuggestingControls ? (
                <>
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
                  Suggesting Controls...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Suggest Additional Controls
                </>
              )}
            </button>
            <button
              onClick={onAddControl}
              disabled={isAddingControl}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingControl ? (
                <>
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  Adding Control...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Control
                </>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {controls.map((control) => (
            <div key={control.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-blue-600">{control.code}</span>
                    {control.aiGenerated && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium">
                        <Brain className="w-3 h-3 inline mr-1" />
                        AI
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getEffectivenessColor(control.effectiveness)}`}>
                      {control.effectiveness}
                    </span>
                  </div>
                  <EditableText
                    value={control.name}
                    onSave={(value) => onUpdateControl(control.id, { name: value })}
                    className="font-medium text-gray-900 mb-2"
                  />
                  <EditableText
                    value={control.description}
                    onSave={(value) => onUpdateControl(control.id, { description: value })}
                    multiline
                    className="text-sm text-gray-600 mb-3"
                  />
                </div>
                <button
                  onClick={() => onDeleteControl(control.id)}
                  className="p-1 text-red-400 hover:text-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Regulatory Framework */}
              {control.regulatory.map((reg, idx) => (
                <div key={idx} className="bg-gray-50 rounded p-3 mb-3">
                  <div className="font-medium text-sm text-gray-900 mb-1">
                    {reg.framework}: {reg.reference}
                  </div>
                  <div className="text-xs text-gray-600">{reg.description}</div>
                </div>
              ))}

              {/* Control Details Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Control Type</div>
                  <EditableText
                    value={control.type}
                    onSave={(value) => onUpdateControl(control.id, { type: value })}
                    className="font-medium"
                  />
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Frequency</div>
                  <EditableText
                    value={control.frequency}
                    onSave={(value) => onUpdateControl(control.id, { frequency: value })}
                    className="font-medium"
                  />
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Owner</div>
                  <EditableText
                    value={control.owner}
                    onSave={(value) => onUpdateControl(control.id, { owner: value })}
                    className="font-medium"
                  />
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Last Tested</div>
                  <EditableText
                    value={control.lastTested}
                    onSave={(value) => onUpdateControl(control.id, { lastTested: value })}
                    className="font-medium"
                  />
                </div>
              </div>

              {/* Issues (if any) */}
              {control.issues && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Issue Identified</span>
                  </div>
                  <div className="text-sm text-yellow-700 mb-2">{control.issues.identified}</div>
                  <div className="text-xs text-yellow-600">
                    <span className="font-medium">Remediation Due:</span> {control.issues.remediationDue}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Review & Validation */}
      <div className="p-6 border-b border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Review & Validation</h4>
        
        <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={risksReviewed}
              onChange={(e) => setRisksReviewed(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-900">Risks reviewed and approved</span>
            {risksReviewed && <CheckCircle2 className="w-4 h-4 text-green-500" />}
          </label>
          
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={controlsReviewed}
              onChange={(e) => setControlsReviewed(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-900">Controls reviewed and approved</span>
            {controlsReviewed && <CheckCircle2 className="w-4 h-4 text-green-500" />}
          </label>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <div><strong>Last Reviewed By:</strong> Not yet reviewed</div>
          <div><strong>Date:</strong> Pending review</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-6">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onEditStep}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            Edit Step
          </button>
          <button
            onClick={onAddRisk}
            disabled={isAddingRisk}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAddingRisk ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                Adding Risk...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Risk
              </>
            )}
          </button>
          <button
            onClick={onAddControl}
            disabled={isAddingControl}
            className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAddingControl ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                Adding Control...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Control
              </>
            )}
          </button>
          <button
            onClick={onViewAuditTrail}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            View Audit Trail
          </button>
        </div>
      </div>
    </div>
  );
};